import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

export type UsagePeriodKind = 'day' | 'week' | 'month' | 'custom' | 'billing';

export type UsagePeriodValue = {
  kind: UsagePeriodKind;
  from: string; // yyyy-MM-dd
  to: string; // yyyy-MM-dd
};

export type BillingContext = {
  startsAt?: string | null;
  endsAt?: string | null;
  paymentOccurrence?: string | null;
};

export const toApiDate = (value: Dayjs | Date | string | null | undefined) => {
  if (!value) return dayjs().format('YYYY-MM-DD');
  const d = dayjs(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
};

export const parseApiDate = (value?: string | Date | Dayjs | null) => {
  if (!value) return dayjs().startOf('day');
  const d = dayjs(value);
  return d.isValid() ? d.startOf('day') : dayjs().startOf('day');
};

/** Clamp day-of-month into the given month (handles 29–31). */
const clampDayInMonth = (year: number, monthIndex: number, day: number) => {
  const max = dayjs(new Date(year, monthIndex + 1, 0)).date();
  return Math.min(Math.max(1, day), max);
};

const addMonthsClamped = (base: Dayjs, months: number, dayOfMonth: number) => {
  const shifted = base.add(months, 'month');
  const day = clampDayInMonth(shifted.year(), shifted.month(), dayOfMonth);
  return shifted.date(day).startOf('day');
};

/**
 * Billing cycle containing `anchor`, based on subscription startsAt + occurrence.
 * Monthly/Yearly: anniversary windows. Custom: startsAt → endsAt (or today).
 */
export const resolveBillingPeriod = (
  billing: BillingContext,
  anchor: Dayjs = dayjs(),
  offsetCycles = 0
): { from: Dayjs; to: Dayjs } | null => {
  const startsAtRaw = billing.startsAt;
  if (!startsAtRaw) return null;
  const startsAt = dayjs(startsAtRaw).startOf('day');
  if (!startsAt.isValid()) return null;

  const occurrence = (billing.paymentOccurrence ?? 'Monthly').toString();
  const endsAt = billing.endsAt ? dayjs(billing.endsAt).startOf('day') : null;
  const today = dayjs().startOf('day');
  const a = anchor.startOf('day');

  if (occurrence === 'Custom') {
    const from = startsAt;
    const to = endsAt?.isValid() ? endsAt : today;
    if (offsetCycles !== 0) {
      // Custom has a single window; no cycle shift.
      return { from, to: to.isBefore(from) ? from : to };
    }
    return { from, to: to.isBefore(from) ? from : to };
  }

    if (occurrence === 'Yearly') {
    const day = startsAt.date();
    const month = startsAt.month();
    let year = a.year();
    let cycleStart = dayjs()
      .year(year)
      .month(month)
      .date(clampDayInMonth(year, month, day))
      .startOf('day');
    if (a.isBefore(cycleStart)) {
      year -= 1;
      cycleStart = dayjs()
        .year(year)
        .month(month)
        .date(clampDayInMonth(year, month, day))
        .startOf('day');
    }
    if (offsetCycles !== 0) {
      cycleStart = cycleStart.add(offsetCycles, 'year');
      cycleStart = cycleStart
        .date(clampDayInMonth(cycleStart.year(), cycleStart.month(), day))
        .startOf('day');
    }
    const nextStart = cycleStart.add(1, 'year');
    const next = nextStart
      .date(clampDayInMonth(nextStart.year(), nextStart.month(), day))
      .startOf('day');
    const cycleEnd = next.subtract(1, 'day');
    return { from: cycleStart, to: cycleEnd };
  }

  // Monthly (default)
  const dayOfMonth = startsAt.date();
  let cycleStart = addMonthsClamped(a.startOf('month'), 0, dayOfMonth);
  // If anniversary day is after anchor day in this month, we're still in previous cycle
  // unless anchor day >= anniversary — build from anchor month first.
  cycleStart = addMonthsClamped(
    dayjs().year(a.year()).month(a.month()).date(1),
    0,
    dayOfMonth
  );
  if (a.isBefore(cycleStart)) {
    cycleStart = addMonthsClamped(cycleStart, -1, dayOfMonth);
  }
  if (offsetCycles !== 0) {
    cycleStart = addMonthsClamped(cycleStart, offsetCycles, dayOfMonth);
  }
  const nextStart = addMonthsClamped(cycleStart, 1, dayOfMonth);
  const cycleEnd = nextStart.subtract(1, 'day');
  return { from: cycleStart, to: cycleEnd };
};

export const resolvePeriod = (
  kind: UsagePeriodKind,
  options?: {
    anchor?: Dayjs | string | Date;
    customFrom?: string | null;
    customTo?: string | null;
    billing?: BillingContext | null;
  }
): UsagePeriodValue => {
  const anchor = dayjs(options?.anchor ?? undefined).startOf('day');

  if (kind === 'day') {
    return { kind, from: toApiDate(anchor), to: toApiDate(anchor) };
  }

  if (kind === 'week') {
    const from = anchor.startOf('isoWeek');
    const to = anchor.endOf('isoWeek');
    return { kind, from: toApiDate(from), to: toApiDate(to) };
  }

  if (kind === 'month') {
    const from = anchor.startOf('month');
    const to = anchor.endOf('month');
    return { kind, from: toApiDate(from), to: toApiDate(to) };
  }

  if (kind === 'billing') {
    const billing = options?.billing;
    const resolved = billing
      ? resolveBillingPeriod(billing, anchor, 0)
      : null;
    if (resolved) {
      return {
        kind,
        from: toApiDate(resolved.from),
        to: toApiDate(resolved.to),
      };
    }
    // Fallback to month if billing context missing
    return resolvePeriod('month', { anchor });
  }

  // custom
  let from = parseApiDate(options?.customFrom ?? options?.anchor);
  let to = parseApiDate(options?.customTo ?? options?.anchor);
  if (to.isBefore(from)) {
    const tmp = from;
    from = to;
    to = tmp;
  }
  return { kind: 'custom', from: toApiDate(from), to: toApiDate(to) };
};

export const createDefaultUsagePeriod = (
  billing?: BillingContext | null
): UsagePeriodValue => {
  if (billing?.startsAt) {
    return resolvePeriod('billing', { billing });
  }
  return resolvePeriod('month');
};

export const shiftPeriod = (
  value: UsagePeriodValue,
  direction: -1 | 1,
  billing?: BillingContext | null
): UsagePeriodValue => {
  if (value.kind === 'custom') {
    const from = parseApiDate(value.from);
    const to = parseApiDate(value.to);
    const days = Math.max(0, to.diff(from, 'day'));
    const nextFrom = from.add(direction * (days + 1), 'day');
    const nextTo = nextFrom.add(days, 'day');
    return {
      kind: 'custom',
      from: toApiDate(nextFrom),
      to: toApiDate(nextTo),
    };
  }

  if (value.kind === 'day') {
    const next = parseApiDate(value.from).add(direction, 'day');
    return resolvePeriod('day', { anchor: next });
  }

  if (value.kind === 'week') {
    const next = parseApiDate(value.from).add(direction, 'week');
    return resolvePeriod('week', { anchor: next });
  }

  if (value.kind === 'month') {
    const next = parseApiDate(value.from).add(direction, 'month');
    return resolvePeriod('month', { anchor: next });
  }

  // billing
  const currentFrom = parseApiDate(value.from);
  const resolved = billing
    ? resolveBillingPeriod(billing, currentFrom, direction)
    : null;
  if (resolved) {
    return {
      kind: 'billing',
      from: toApiDate(resolved.from),
      to: toApiDate(resolved.to),
    };
  }
  return resolvePeriod('month', {
    anchor: currentFrom.add(direction, 'month'),
  });
};

export const formatPeriodLabel = (value: UsagePeriodValue) => {
  const from = parseApiDate(value.from);
  const to = parseApiDate(value.to);
  const sameDay = from.isSame(to, 'day');
  if (sameDay) {
    return from.format('YYYY. MM. DD.');
  }
  if (from.isSame(to, 'year')) {
    return `${from.format('YYYY. MM. DD.')} – ${to.format('MM. DD.')}`;
  }
  return `${from.format('YYYY. MM. DD.')} – ${to.format('YYYY. MM. DD.')}`;
};

export const canUseBillingPeriod = (billing?: BillingContext | null) =>
  Boolean(billing?.startsAt);
